import { useAskInputSearchModel } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputSearchModel.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useGateSearchModelSelection } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useGateSearchModelSelection.js';
import { useSharedSelectSearchModel } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useSharedSelectSearchModel.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useCallback, useMemo } from 'react';
/**
 * Adapts the ask input's search model and search mode state into the shape
 * consumed by the right-attribution model picker.
 *
 * The controller supports both uncontrolled ask inputs that read/write the
 * shared ThreadSettingsProvider model and controlled ask inputs that receive a
 * model plus callbacks from their parent. It also centralizes the legacy
 * selection side effects the picker still needs: capability gating, paywall
 * upsells, cross-window model broadcasts, and per-mode model-picker props.
 */
export const useAskInputSearchModeModelController = ({ searchModel, onSearchModelSelect, onSearchModelDeselect, }) => {
    // This is the thread-level mode inferred from ThreadSettingsProvider's
    // configuredModel. Keep it named internalSearchMode because a controlled
    // searchModel prop below can override what the picker should display as the
    // selectedSearchMode.
    const internalSearchMode = useSelectedSearchMode();
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    const selectedSearchMode = useMemo(() => {
        if (searchModel !== undefined) {
            return getSearchModeForSearchModel(searchModel);
        }
        return internalSearchMode;
    }, [getSearchModeForSearchModel, internalSearchMode, searchModel]);
    const searchModelProps = useAskInputSearchModel({
        searchMode: selectedSearchMode,
        searchModel,
        onSearchModelSelect,
        onSearchModelDeselect,
    });
    // configuredModel is the current thread model from ThreadSettingsProvider:
    // either the user's explicit model override or the initialized default model.
    // Controlled ask inputs ignore it for display and use searchModel instead.
    const { configuredModel: threadConfiguredModel } = useThreadConfig();
    const selectedModel = searchModel ?? threadConfiguredModel;
    const openPaywallUpsell = usePaywallUpsell();
    // These hooks intentionally couple model changes to the existing ask-wide
    // selection behavior. The picker is not just updating a local value:
    // selecting a model may open auth/paywall UI, reject gated models, persist the
    // model in ThreadSettingsProvider, and broadcast the change to native/other
    // windows. Controlled callers can avoid the persistence/broadcast path with
    // onSearchModelSelect, but gating is still shared so the same model rules
    // apply to both controlled and uncontrolled inputs.
    const setSelectedSearchModel = useSharedSelectSearchModel({
        openPaywallUpsell,
    });
    const { gateSearchModelSelection } = useGateSearchModelSelection({
        openPaywallUpsell,
    });
    const setSelectedModel = useCallback((model) => {
        const isGated = gateSearchModelSelection(model);
        if (isGated) {
            return false;
        }
        if (onSearchModelSelect) {
            onSearchModelSelect(model);
            return true;
        }
        return setSelectedSearchModel(
        // this `as SearchModel` is part of the dynamic model config migration
        // the new `SearchModelMenu` component uses the dynamic config and uses type `string` for models
        model);
    }, [gateSearchModelSelection, onSearchModelSelect, setSelectedSearchModel]);
    return useMemo(() => ({
        searchModelProps,
        selectedSearchMode,
        selectedModel,
        setSelectedModel,
    }), [searchModelProps, selectedSearchMode, selectedModel, setSelectedModel]);
};
//# sourceMappingURL=useAskInputSearchModeModelController.js.map